'use client';

import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import { useUser, useClerk } from '@clerk/nextjs';
import { useAuthActions } from '@convex-dev/auth/react';
import { useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';

// ============================================================================
// LA SESSIONE, UNA SOLA PER TUTTA L'APP.
//
// L'app non deve sapere chi tiene l'identita': chiede useSession() e ottiene
// sempre le stesse quattro cose (chi sei, se e' pronto, come esci).
//
// Il trucco sta qui sotto: i due ponti. Le regole di React vietano di chiamare
// un hook dentro un if, e chiamare gli hook di Clerk senza il suo provider
// lancia. Quindi si monta UN SOLO ponte, quello della modalita' attiva, e il
// ponte pubblica i dati nel contesto. Chi legge non chiama mai un hook di
// libreria: legge il contesto e basta.
// ============================================================================

export type Session = {
  /** Id dell'utente, o null se non e' loggato. */
  userId: string | null;
  email: string | null;
  fullName: string | null;
  /** Millisecondi. Serve solo a mostrare "iscritto il ...". */
  createdAt: number | null;
  /** Finche' e' false non si sa ancora se c'e' un utente: non decidere nulla. */
  isLoaded: boolean;
  signOut: (redirectTo?: string) => Promise<void>;
};

const EMPTY: Session = {
  userId: null,
  email: null,
  fullName: null,
  createdAt: null,
  isLoaded: false,
  signOut: async () => {},
};

const SessionContext = createContext<Session>(EMPTY);

/** La sessione corrente. Funziona identica nelle due modalita'. */
export function useSession(): Session {
  return useContext(SessionContext);
}

// ── Ponte per il cloud: l'identita' arriva da Clerk ─────────────────────────
export function ClerkSessionBridge({ children }: { children: ReactNode }) {
  const { user, isLoaded } = useUser();
  const { signOut: clerkSignOut } = useClerk();

  const signOut = useCallback(
    async (redirectTo = '/login') => {
      await clerkSignOut({ redirectUrl: redirectTo });
    },
    [clerkSignOut],
  );

  const value = useMemo<Session>(
    () => ({
      userId: user?.id ?? null,
      email: user?.primaryEmailAddress?.emailAddress ?? null,
      fullName: user?.fullName ?? user?.firstName ?? null,
      createdAt: user?.createdAt ? new Date(user.createdAt).getTime() : null,
      isLoaded,
      signOut,
    }),
    [user, isLoaded, signOut],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

// ── Ponte per l'installazione del cliente: l'identita' vive in Convex ───────
export function LocalSessionBridge({ children }: { children: ReactNode }) {
  const { signOut: convexSignOut } = useAuthActions();
  // Con il login interno il profilo sta nel database del cliente: la query
  // torna null quando non c'e' nessuno collegato, undefined finche' carica.
  const me = useQuery(api.users.me);

  const signOut = useCallback(
    async (redirectTo = '/login') => {
      await convexSignOut();
      // Convex Auth non reindirizza da solo: senza questo si resta su una
      // pagina dell'app senza piu' sessione.
      if (typeof window !== 'undefined') window.location.href = redirectTo;
    },
    [convexSignOut],
  );

  const value = useMemo<Session>(
    () => ({
      userId: me?.id ?? null,
      email: me?.email ?? null,
      fullName: me?.fullName ?? null,
      createdAt: me?.createdAt ?? null,
      isLoaded: me !== undefined,
      signOut,
    }),
    [me, signOut],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
